import { CurrencyUtils } from '@Core/Currencies/CurrencyUtils';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { GameScene } from '@Core/GameScene/GameScene';
import { TextContainer } from '@Core/TextContainer/TextContainer';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ILockAndRollAssetsConfig } from '@GFTypes/GameStoreTypes/LockAndRollAssetsConfig';
import { EButtonEvents } from '@Managers/Events/ButtonEvents';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EntryCondition } from '@Managers/StateMachine/EntryCondition';
import { LeaveCondition } from '@Managers/StateMachine/LeaveCondition';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateManager } from '@Managers/StateManager/StateManager';
import { DeferredPromise } from '@Tools/DeferredPromise';
import { isBeforeFirstLockAndRollSpin, isLastLockAndRollSpin } from '../SpecialGamesHelper';

export class LockAndRollCollection extends GameComponent {
    private readonly collectionIntroLeaveCondition = new LeaveCondition<EGameStates>('waitForCollectionIntro');
    private readonly collectionLeaveCondition = new LeaveCondition<EGameStates>('waitForCollection');
    private readonly skipPromise = new DeferredPromise();
    private counterText: TextContainer;
    private collectionText: TextContainer;
    private coinCount: number = 0;
    constructor(
        public readonly scene: GameScene,
        private readonly assetsConfig: ILockAndRollAssetsConfig
    ) {
        super(scene);
        Logger.log(LoggerLevel.Constructor, { class: LockAndRollCollection.name, source: LoggerSource.Game, message: 'LockAndRollCollection constructor' });
    }

    public onCreate(): void {
        const isFirstLockAndRollSpin = new EntryCondition<EGameStates>('checkIfIsStartOfSpecialGames', () => {
            return isBeforeFirstLockAndRollSpin(this.gameStore);
        });
        const isLastLRSpin = new EntryCondition<EGameStates>('checkIfLastLRSpin', () => {
            return isLastLockAndRollSpin(this.gameStore);
        });

        const preSubStates = {
            [EGameStates.SPECIAL_GAMES_COLLECT_INTRO]: {
                entryConditions: [isLastLRSpin],
                leaveConditions: [this.collectionLeaveCondition]
            }
        };

        const postSubStates = {
            [EGameStates.SPECIAL_GAMES_COUNTER_SHOW]: {
                entryConditions: [isFirstLockAndRollSpin],
                leaveConditions: [this.collectionIntroLeaveCondition]
            }
        };

        StateManager.assignSubStates(EGameStates.PRE_WIN_FEATURES_STATE, preSubStates);
        StateManager.assignSubStates(EGameStates.POST_WIN_FEATURES_STATE, postSubStates);

        EventsCenter.on(EGameStates.SPECIAL_GAMES_COUNTER_SHOW, this.onPreCounterShow, this);
        EventsCenter.on(EGameStates.POST_SPIN_STATE, this.onSpinResult, this);
        EventsCenter.on(EGameStates.SPECIAL_GAMES_COLLECT_INTRO, this.onPreCounterIntro, this);
        EventsCenter.on(EButtonEvents.SKIP_BUTTON_PRESSED, this.onSkip, this);

        this.counterText = TextContainer.fromSlot(this.assetsConfig.counterTextSlotName, this.scene, '');
        this.scene.specialGamesSpine?.slotManager.assignObjectToSlot(this.assetsConfig.counterTextSlotName, this.counterText);

        this.collectionText = TextContainer.fromSlot(this.assetsConfig.collectionTextSlotName, this.scene, '');
        this.scene.specialGamesSpine?.slotManager.assignObjectToSlot(this.assetsConfig.collectionTextSlotName, this.collectionText);
    }

    private async onPreCounterShow(): Promise<void> {
        this.coinCount = this.gameStore.get(EGameStoreKeys.LockAndRoll).symbols.length;
        this.counterText.setText(`${this.coinCount}`);
        this.counterText.zIndex = this.scene.specialGamesSpine.spine.zIndex + 1;
        this.collectionText.setText('');
        this.skipPromise.reset();
        const animPromise = this.scene.specialGamesSpine?.waitForAnimationEvent(EGameStates.SPECIAL_GAMES_COUNTER_SHOW, 'complete');
        if (!this.scene.specialGamesSpine.eventManager.hasGameEventAnimation(EGameStates.SPECIAL_GAMES_COUNTER_SHOW, true)) {
            this.skipPromise.resolve();
        }
        await Promise.race([animPromise.promise, this.skipPromise.promise]);
        animPromise.cancel();
        this.collectionIntroLeaveCondition.resolve();
    }

    private onSpinResult(): void {
        const newCoinCount = this.gameStore.get(EGameStoreKeys.LockAndRoll).symbols.length;
        if (newCoinCount !== this.coinCount) {
            this.coinCount = newCoinCount;
            EventsCenter.emit(EGameEvents.LOCK_AND_ROLL_COIN_COUNT_CHANGE);
            this.counterText.setText(`${this.coinCount}`);
        }
    }

    private async onPreCounterIntro(): Promise<void> {
        this.skipPromise.reset();
        const animPromise = this.scene.specialGamesSpine?.waitForAnimationEvent(EGameStates.SPECIAL_GAMES_COLLECT_INTRO, 'complete');
        await animPromise.promise;
        animPromise.cancel();

        this.collectionText.zIndex = this.scene.specialGamesSpine.spine.zIndex + 1;

        const coins = [...this.gameStore.get(EGameStoreKeys.LockAndRoll).symbols].sort((coinA, coinB) => coinA.win - coinB.win);
        const symbolsPerReel = this.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel);
        let win = 0;
        let spineSpeed = 3;
        this.scene.specialGamesSpine?.setSpeed(spineSpeed);

        for (const coin of coins) {
            const collectId = coin.index + 1 + coin.reel * symbolsPerReel;
            const animPromise = this.scene.specialGamesSpine?.waitForAnimationEvent(EGameEvents.LR_POS_$, 'complete', [collectId]);
            const stickySymbol = this.scene.stickySymbols.stickySymbols[coin.reel][coin.index];

            this.skipPromise.reset();

            stickySymbol.symbolAnimation.eventManager.playEventAnimation('SYMBOL_COLLECTED_IN');
            stickySymbol.symbolAnimation?.setSpeed(spineSpeed);
            await stickySymbol.symbolAnimation.waitForAnimationEvent('SYMBOL_COLLECTED_IN', 'complete').promise;
            stickySymbol.symbolAnimation.eventManager.playEventAnimation('SYMBOL_COLLECTED');
            stickySymbol.symbolAnimation?.setSpeed(1);

            EventsCenter.emit(EGameEvents.LR_POS_$, { eventIds: [collectId] });

            this.skipPromise.promise.then(
                () => {
                    spineSpeed = 6;
                    this.scene.specialGamesSpine?.setSpeed(spineSpeed);
                },
                () => {}
            );

            await animPromise.promise;
            animPromise.cancel();
            this.skipPromise.reject();
            win += coin.win;

            this.collectionText.setText(CurrencyUtils.formatRawCurrencyValue(win));
        }
        this.scene.specialGamesSpine?.setSpeed(1);
        EventsCenter.emit(EGameEvents.SPECIAL_GAMES_COLLECTED);
        this.collectionLeaveCondition.resolve();
    }

    private onSkip(): void {
        this.skipPromise.resolve();
    }
}
